import React, { Suspense, useEffect, useRef, useState, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { ProductModel } from './ProductModel';

const AUTO_ADVANCE_MS = 5000;
const WHEEL_THRESHOLD = 20;
const MOBILE_BREAKPOINT = 600;

export default function HeroCarouselViewer({ products, activeIndex, onChangeIndex }) {
  const isPausedRef = useRef(false);
  const wheelCooldown = useRef(false);
  const [isUserRotating, setIsUserRotating] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handleChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const goTo = useCallback((newIndex) => {
    const total = products.length;
    onChangeIndex(((newIndex % total) + total) % total);
  }, [products.length, onChangeIndex]);

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  useEffect(() => {
    if (products.length <= 1) return;
    const id = setInterval(() => {
      if (!isPausedRef.current) goTo(activeIndex + 1);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [activeIndex, goTo, products.length]);

  const pause = () => { isPausedRef.current = true; };
  const resume = () => { isPausedRef.current = false; };

  const handleWheel = (e) => {
    if (wheelCooldown.current) return;
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (Math.abs(delta) < WHEEL_THRESHOLD) return;
    wheelCooldown.current = true;
    delta > 0 ? goNext() : goPrev();
    setTimeout(() => { wheelCooldown.current = false; }, 400);
  };

  const active = products[activeIndex];
  const leftIndex = (activeIndex - 1 + products.length) % products.length;
  const rightIndex = (activeIndex + 1) % products.length;
  const leftProduct = products[leftIndex];
  const rightProduct = products[rightIndex];

  const centerModelRef = useRef();
  const pointerState = useRef({ active: false, lastX: 0, lastY: 0 });

  const onPointerDown = (e) => {
    if (e.target && e.target.closest && e.target.closest('button')) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
    pointerState.current = { active: true, lastX: e.clientX, lastY: e.clientY };
    setIsUserRotating(true);
    pause();
  };

  const onPointerMove = (e) => {
    if (!pointerState.current.active) return;
    const deltaX = e.clientX - pointerState.current.lastX;
    const deltaY = e.clientY - pointerState.current.lastY;
    pointerState.current.lastX = e.clientX;
    pointerState.current.lastY = e.clientY;
    if (centerModelRef.current) {
      centerModelRef.current.rotation.y += deltaX * 0.007;
      centerModelRef.current.rotation.x += deltaY * 0.005;
      const maxPitch = Math.PI / 2 - 0.05;
      if (centerModelRef.current.rotation.x > maxPitch) centerModelRef.current.rotation.x = maxPitch;
      if (centerModelRef.current.rotation.x < -maxPitch) centerModelRef.current.rotation.x = -maxPitch;
    }
  };

  const onPointerUp = (e) => {
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) {}
    pointerState.current.active = false;
    setIsUserRotating(false);
    resume();
  };

  const viewerHeight = isMobile ? '360px' : '560px';
  // Zoom adjustments: keep mobile unchanged, bring desktop camera closer
  const cameraPosition = isMobile ? [0, 0, 4.5] : [0, 0, 3];
  const cameraFov = isMobile ? 42 : 38;
  const sideOffset = isMobile ? 2.2 : 3.6;
  // Per-product zoom/position helpers (override in data/products.js if needed)
  const defaultDesktopCenterScale = 1.5;
  const defaultMobileCenterScale = 1.1;
  const defaultDesktopSideScale = 1;
  const defaultDesktopCenterZ = 0;
  const defaultDesktopSideZ = -1;

  const getScale = (product, role = 'center') => {
    if (isMobile) return product?.mobileScale ?? (role === 'center' ? defaultMobileCenterScale : product?.mobileScale ?? defaultDesktopSideScale);
    return product?.desktopScale ?? (role === 'center' ? defaultDesktopCenterScale : defaultDesktopSideScale);
  };

  const getZ = (product, role = 'center') => {
    if (isMobile) return product?.mobileZ ?? 0;
    return product?.desktopZ ?? (role === 'center' ? defaultDesktopCenterZ : defaultDesktopSideZ);
  };

  return (
    <div
      className="carousel-viewer"
      style={{ height: viewerHeight, width: '100%' }}
      onWheel={handleWheel}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="viewer-title-row">
        {!isMobile && <span className="carousel-title carousel-title-side">{leftProduct.name}</span>}
        <span className="carousel-title carousel-title-center">{active.name}</span>
        {!isMobile && <span className="carousel-title carousel-title-side">{rightProduct.name}</span>}
      </div>

      <Canvas camera={{ position: cameraPosition, fov: cameraFov }} gl={{ alpha: true }}>
        <Environment preset="studio" />
        <Suspense fallback={null}>
          {!isMobile && (
            <ProductModel
              key={`l-${leftProduct.model}`}
              path={leftProduct.model}
              autoRotate
              rotationSpeed={0.4}
              position={[-sideOffset, -0.2, getZ(leftProduct, 'side')]}
              scale={getScale(leftProduct, 'side')}
            />
          )}

            <ProductModel
            key={`c-${active.model}`}
            path={active.model}
            autoRotate
            rotationSpeed={0.4}
            paused={isUserRotating}
            position={[0, -0.2, getZ(active, 'center')]}
            // make center model larger on desktop for stronger presence (per-product overrides supported)
            scale={getScale(active, 'center')}
            groupRef={centerModelRef}
          />

          {!isMobile && (
            <ProductModel
              key={`r-${rightProduct.model}`}
              path={rightProduct.model}
              autoRotate
              rotationSpeed={0.4}
              position={[sideOffset, -0.2, getZ(rightProduct, 'side')]}
              scale={getScale(rightProduct, 'side')}
            />
          )}

          <ContactShadows position={[0, -1.3, 0]} opacity={0.35} scale={12} blur={3} />
        </Suspense>

        <OrbitControls
          enablePan={false}
          enableZoom={true}
          minDistance={isMobile ? 3 : 5}
          maxDistance={isMobile ? 7 : 12}
          enableRotate={false}
        />
      </Canvas>

      {products.length > 1 && (
        <>
          <button className="carousel-arrow carousel-arrow-left" onClick={goPrev} aria-label="Previous product">
            &#8249;
          </button>
          <button className="carousel-arrow carousel-arrow-right" onClick={goNext} aria-label="Next product">
            &#8250;
          </button>

          <div className="carousel-dots">
            {products.map((p, i) => (
              <button
                key={p.id}
                className={`carousel-dot ${i === activeIndex ? 'active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`View ${p.name}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}